'use client';

import { c as _c } from "react/compiler-runtime";
import { ReadOnly, withFieldGroup } from '@repo/form';
import type { LocationPlaceValue } from '@repo/features/location';
import { TimePickerInput, TimePickerProvider, type TimePickerValue, type TimeType } from '@repo/features/time-picker';
import type { Coordinates, GoogleLocationResult, PastedLocationResult } from '@repo/geolocation';
import { Badge, Button, Field, FieldContent, FieldError, FieldLabel, Input, Kbd, KbdGroup, Popover, PopoverContent, PopoverTrigger, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui/components';
import { ArrowDown, ArrowUp, ChevronDown, Download, Repeat2, Trash2, Upload } from '@repo/ui/icons';
import type { KeyboardEvent, ReactNode, Ref } from 'react';
import { useEffect, useRef } from 'react';
import type { QuickCreateStop } from './_utils/route-model.ts';
export type QuickCreateLocationCreationRequest = {
  companyId: number;
  googlePlace?: GoogleLocationResult;
  onCreated: (_place: LocationPlaceValue) => void;
  organisationId?: number;
  pastedLocation?: PastedLocationResult;
  query: string;
  stopId: string;
};
export type QuickCreateLocationEditRequest = {
  companyId: number;
  locationId: number;
  onUpdated: (_place: LocationPlaceValue) => void;
  organisationId?: number;
  stopId: string;
};

/** The time window a Stop starts with when the user picks `type`. */
export const getStopTimeValue = (type: TimeType): TimePickerValue => {
  switch (type) {
    case '24':
      return {
        type: '24'
      };
    case 'asap':
      return {
        type: 'asap'
      };
    case 'at':
      return {
        from: '08:00',
        type: 'at'
      };
    case 'between':
      return {
        from: '08:00',
        to: '16:00',
        type: 'between'
      };
    case 'by':
      return {
        to: '16:00',
        type: 'by'
      };
    case 'tbc':
      return {
        type: 'tbc'
      };
  }
};

/** A time type's name, as the time menus show it. */
export const getTimeLabel = (type: TimeType) => {
  switch (type) {
    case '24':
      return '24 Hours';
    case 'asap':
      return 'ASAP';
    case 'at':
      return 'At';
    case 'between':
      return 'Between';
    case 'by':
      return 'Before';
    case 'tbc':
      return 'TBC';
  }
};

/** A Stop as the field edits it. Its date may stay empty where the caller allows that. */
export type StopFieldValues = Omit<QuickCreateStop, 'date'> & {
  date: Date | undefined;
};
const QUICK_CREATE_STOP_DEFAULT: StopFieldValues = {
  date: new Date(),
  id: 'quick-create-stop-default',
  place: null,
  reference_no: null,
  time: {
    type: 'asap'
  },
  type: 'collection'
};
type QuickCreateStopFieldProps = {
  actions?: StopActions;
  active?: boolean;
  companyId?: null | number;
  /** Whether the date is required. Defaults to true. */
  dateRequired?: boolean;
  dateTriggerRef?: Ref<HTMLButtonElement>;
  locationTriggerRef?: Ref<HTMLButtonElement>;
  /** The earliest date the calendar offers. */
  minDate?: Date;
  nearbyCoordinates?: Coordinates;
  onCreateLocation?: (_request: QuickCreateLocationCreationRequest) => void;
  onDateChange?: (_date: Date | undefined) => void;
  onEditLocation?: (_request: QuickCreateLocationEditRequest) => void;
  onFocusStop?: () => void;
  onOpenTimeMenuChange: (_open: boolean) => void;
  onSelectTimeType: (_type: TimeType) => void;
  organisationId?: number;
  pending: boolean;
  /** Shows the location as text, for a Stop whose location can't change here. */
  readOnlyLocation?: boolean;
  /** Shows the dialog's keyboard shortcuts beside the labels. Defaults to true. */
  shortcutHints?: boolean;
  stopId: string;
  stopNumber: number;
  timeMenuOpen: boolean;
  /** The time types offered, in menu order; digit keys pick them in the open menu. */
  timeTypes: readonly TimeType[];
};

/** Route editing for an interior Stop: toggle its type, move it, or delete it. */
type StopActions = {
  canDelete: boolean;
  canMoveDown: boolean;
  canMoveUp: boolean;
  onDelete: () => void;
  onMoveDown: () => void;
  onMoveUp: () => void;
  onToggleType: () => void;
};
const QUICK_CREATE_STOP_FIELD_PROPS: QuickCreateStopFieldProps = {
  actions: undefined,
  active: false,
  companyId: null,
  dateRequired: true,
  dateTriggerRef: undefined,
  locationTriggerRef: undefined,
  minDate: undefined,
  nearbyCoordinates: undefined,
  onCreateLocation: undefined,
  onDateChange: undefined,
  onEditLocation: undefined,
  onFocusStop: undefined,
  onOpenTimeMenuChange: () => undefined,
  onSelectTimeType: () => undefined,
  organisationId: undefined,
  pending: false,
  readOnlyLocation: false,
  shortcutHints: true,
  stopId: 'quick-create-stop-default',
  stopNumber: 1,
  timeMenuOpen: false,
  timeTypes: []
};
const StopAction = t0 => {
  const $ = _c(18);
  const {
    children,
    disabled,
    label,
    onClick,
    shortcut
  } = t0;
  let t1;
  if ($[0] !== disabled || $[1] !== label || $[2] !== onClick) {
    t1 = <Button aria-label={label} disabled={disabled} onClick={onClick} type="button" variant="ghost" />;
    $[0] = disabled;
    $[1] = label;
    $[2] = onClick;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== shortcut) {
    t2 = <Kbd data-sentry-element="Kbd" data-sentry-source-file="quick-create-stop-field.tsx">{shortcut}</Kbd>;
    $[4] = shortcut;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== children || $[7] !== t1 || $[8] !== t2) {
    t3 = <TooltipTrigger render={t1} data-sentry-element="TooltipTrigger" data-sentry-source-file="quick-create-stop-field.tsx">{children}{t2}</TooltipTrigger>;
    $[6] = children;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== shortcut) {
    t4 = <Kbd data-sentry-element="Kbd" data-sentry-source-file="quick-create-stop-field.tsx">{shortcut}</Kbd>;
    $[10] = shortcut;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== label || $[13] !== t4) {
    t5 = <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="quick-create-stop-field.tsx">{label}{t4}</TooltipContent>;
    $[12] = label;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t3 || $[16] !== t5) {
    t6 = <div className="flex items-center gap-0.5" data-sentry-component="StopAction" data-sentry-source-file="quick-create-stop-field.tsx"><Tooltip data-sentry-element="Tooltip" data-sentry-source-file="quick-create-stop-field.tsx">{t3}{t5}</Tooltip></div>;
    $[15] = t3;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
const QuickCreateTimeControl = t0 => {
  const $ = _c(51);
  const {
    disabled,
    errors,
    onBlur,
    onChange,
    onOpenChange,
    onSelectTimeType,
    open,
    shortcutHints,
    stopId,
    stopNumber,
    timeTypes,
    value
  } = t0;
  const menuRef = useRef(null);
  const isInvalid = errors.length > 0;
  let t1;
  let t2;
  if ($[0] !== open) {
    t1 = () => {
      if (!open) {
        return;
      }
      const frame = requestAnimationFrame(() => {
        const firstItem = menuRef.current?.querySelector("[role=\"menuitem\"]");
        firstItem?.focus();
      });
      return () => cancelAnimationFrame(frame);
    };
    t2 = [open];
    $[0] = open;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== onSelectTimeType || $[4] !== timeTypes) {
    t3 = event => {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        const menuItems = Array.from(menuRef.current?.querySelectorAll("[role=\"menuitem\"]") ?? []);
        if (menuItems.length === 0 || !(event.target instanceof HTMLButtonElement)) {
          return;
        }
        const activeIndex = menuItems.indexOf(event.target);
        if (activeIndex < 0) {
          return;
        }
        event.preventDefault();
        const nextIndex = event.key === "ArrowDown" ? (activeIndex + 1) % menuItems.length : (activeIndex - 1 + menuItems.length) % menuItems.length;
        menuItems[nextIndex]?.focus();
        return;
      }
      const type = /^[1-9]$/.test(event.key) ? timeTypes[Number(event.key) - 1] : undefined;
      if (!type) {
        return;
      }
      event.preventDefault();
      onSelectTimeType(type);
    };
    $[3] = onSelectTimeType;
    $[4] = timeTypes;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const selectTimeTypeByShortcut = t3;
  let t4;
  if ($[6] !== shortcutHints) {
    t4 = shortcutHints ? <KbdGroup className="ml-2"><Kbd>⇧T</Kbd></KbdGroup> : null;
    $[6] = shortcutHints;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <FieldLabel data-sentry-element="FieldLabel" data-sentry-source-file="quick-create-stop-field.tsx">Time *{t4}</FieldLabel>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = `quick-create-time-${stopId}`;
  const t7 = `Choose time type for Stop ${stopNumber}`;
  let t8;
  if ($[10] !== value.type) {
    t8 = getTimeLabel(value.type);
    $[10] = value.type;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ChevronDown aria-hidden={true} className="size-4 text-muted-foreground" data-sentry-element="ChevronDown" data-sentry-source-file="quick-create-stop-field.tsx" />;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== disabled || $[14] !== isInvalid || $[15] !== t7 || $[16] !== t8) {
    t10 = <PopoverTrigger asChild={true} data-sentry-element="PopoverTrigger" data-sentry-source-file="quick-create-stop-field.tsx"><Button aria-invalid={isInvalid} aria-label={t7} disabled={disabled} type="button" variant="outline" data-sentry-element="Button" data-sentry-source-file="quick-create-stop-field.tsx">{t8}{t9}</Button></PopoverTrigger>;
    $[13] = disabled;
    $[14] = isInvalid;
    $[15] = t7;
    $[16] = t8;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const t11 = `Time type choices for Stop ${stopNumber}`;
  let t12;
  if ($[18] !== onSelectTimeType || $[19] !== timeTypes) {
    let t13;
    if ($[21] !== onSelectTimeType) {
      t13 = (type_0, index) => <Button className="w-full justify-start" key={type_0} onClick={() => onSelectTimeType(type_0)} role="menuitem" type="button" variant="ghost"><span className="mr-2 text-muted-foreground">{index + 1}</span>{getTimeLabel(type_0)}</Button>;
      $[21] = onSelectTimeType;
      $[22] = t13;
    } else {
      t13 = $[22];
    }
    t12 = timeTypes.map(t13);
    $[18] = onSelectTimeType;
    $[19] = timeTypes;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[23] !== selectTimeTypeByShortcut || $[24] !== t11 || $[25] !== t12) {
    t13 = <PopoverContent align="start" className="w-48 p-1" data-sentry-element="PopoverContent" data-sentry-source-file="quick-create-stop-field.tsx"><div aria-label={t11} data-quick-create-menu={true} onKeyDown={selectTimeTypeByShortcut} ref={menuRef} role="menu" tabIndex={-1}>{t12}</div></PopoverContent>;
    $[23] = selectTimeTypeByShortcut;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== onOpenChange || $[28] !== open || $[29] !== t10 || $[30] !== t13) {
    t14 = <Popover onOpenChange={onOpenChange} open={open} data-sentry-element="Popover" data-sentry-source-file="quick-create-stop-field.tsx">{t10}{t13}</Popover>;
    $[27] = onOpenChange;
    $[28] = open;
    $[29] = t10;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <TimePickerInput data-sentry-element="TimePickerInput" data-sentry-source-file="quick-create-stop-field.tsx" />;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== onBlur || $[34] !== t14 || $[35] !== t6) {
    t16 = <div className="flex flex-wrap items-center gap-2 [&>[data-slot=input-group]]:bg-background [&>[data-slot=input-group]]:shadow-none" id={t6} onBlurCapture={onBlur}>{t14}{t15}</div>;
    $[33] = onBlur;
    $[34] = t14;
    $[35] = t6;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== isInvalid || $[38] !== onChange || $[39] !== t16 || $[40] !== value) {
    t17 = <TimePickerProvider invalid={isInvalid} mode="schema" onChange={onChange} value={value} data-sentry-element="TimePickerProvider" data-sentry-source-file="quick-create-stop-field.tsx">{t16}</TimePickerProvider>;
    $[37] = isInvalid;
    $[38] = onChange;
    $[39] = t16;
    $[40] = value;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== errors) {
    t18 = <FieldError errors={errors} data-sentry-element="FieldError" data-sentry-source-file="quick-create-stop-field.tsx" />;
    $[42] = errors;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t17 || $[45] !== t18) {
    t19 = <FieldContent data-sentry-element="FieldContent" data-sentry-source-file="quick-create-stop-field.tsx">{t17}{t18}</FieldContent>;
    $[44] = t17;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== isInvalid || $[48] !== t19 || $[49] !== t5) {
    t20 = <Field className="gap-1" data-invalid={isInvalid} data-sentry-element="Field" data-sentry-component="QuickCreateTimeControl" data-sentry-source-file="quick-create-stop-field.tsx">{t5}{t19}</Field>;
    $[47] = isInvalid;
    $[48] = t19;
    $[49] = t5;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  return t20;
};

/**
 * One Stop's location, reference, date and time. Callers choose the time types, the date bounds
 * and whether the date is required, and validate the Stop themselves.
 */
export const QuickCreateStopField = withFieldGroup({
  defaultValues: QUICK_CREATE_STOP_DEFAULT,
  props: QUICK_CREATE_STOP_FIELD_PROPS,
  render: ({
    actions,
    active = false,
    companyId = null,
    dateRequired = true,
    dateTriggerRef,
    group,
    locationTriggerRef,
    minDate,
    nearbyCoordinates,
    onCreateLocation,
    onDateChange,
    onEditLocation,
    onFocusStop,
    onOpenTimeMenuChange,
    onSelectTimeType,
    organisationId,
    pending,
    readOnlyLocation = false,
    shortcutHints = true,
    stopId,
    stopNumber,
    timeMenuOpen,
    timeTypes
  }) => {
    const setPlace = (place: LocationPlaceValue) => {
      group.setFieldValue('place', {
        ...place,
        country_code: place.country_code ?? undefined
      });
    };
    const createLocation = ({
      googlePlace,
      pastedLocation,
      query
    }: {
      googlePlace?: GoogleLocationResult;
      pastedLocation?: PastedLocationResult;
      query: string;
    }) => {
      if (companyId == null || !onCreateLocation) return;
      onCreateLocation({
        companyId,
        googlePlace,
        onCreated: setPlace,
        organisationId,
        pastedLocation,
        query,
        stopId
      });
    };
    const editLocation = (place: LocationPlaceValue) => {
      if (companyId == null || place.location_id == null || !onEditLocation) return;
      onEditLocation({
        companyId,
        locationId: place.location_id,
        onUpdated: setPlace,
        organisationId,
        stopId
      });
    };
    return <section aria-label={`Stop ${stopNumber}`} className="rounded-lg border p-3 focus-within:ring-2 focus-within:ring-ring focus-within:ring-inset data-[active=true]:bg-accent/50" data-active={active} data-quick-create-stop onFocusCapture={onFocusStop}>
        <fieldset className="flex min-w-0 flex-col gap-3" disabled={pending}>
          <div aria-label={`Stop ${stopNumber} header`} className="flex min-w-0 flex-wrap items-center justify-between gap-3 rounded-sm outline-none" data-quick-create-focus={`drops.${stopNumber - 1}`} data-quick-create-stop-header id={`quick-create-stop-${stopId}`} role="group" tabIndex={0}>
            <div className="flex min-w-0 items-center gap-2">
              <Badge className="size-5 rounded-full px-0 py-0">{stopNumber}</Badge>
              <div className="flex items-center gap-1.5 font-medium">
                {group.state.values.type === 'collection' ? <Download /> : <Upload />}
                {group.state.values.type === 'collection' ? 'Collection' : 'Delivery'}
              </div>
            </div>
            {actions ? <div className="flex shrink-0 flex-wrap items-center gap-1">
                <StopAction disabled={pending} label={`Toggle Stop ${stopNumber} type`} onClick={actions.onToggleType} shortcut="ST">
                  <Repeat2 />
                </StopAction>
                <StopAction disabled={pending || !actions.canMoveUp} label={`Move Stop ${stopNumber} up`} onClick={actions.onMoveUp} shortcut="SU">
                  <ArrowUp />
                </StopAction>
                <StopAction disabled={pending || !actions.canMoveDown} label={`Move Stop ${stopNumber} down`} onClick={actions.onMoveDown} shortcut="SD">
                  <ArrowDown />
                </StopAction>
                <StopAction disabled={pending || !actions.canDelete} label={`Delete Stop ${stopNumber}`} onClick={actions.onDelete} shortcut="SX">
                  <Trash2 />
                </StopAction>
              </div> : null}
          </div>

          <div className="grid min-w-0 gap-2 @min-[34rem]/quick-create:grid-cols-[minmax(12rem,1.2fr)_minmax(10rem,1fr)] @min-[54rem]/quick-create:grid-cols-[minmax(12rem,1.2fr)_minmax(10rem,1fr)_minmax(9rem,1fr)_minmax(17rem,1.5fr)]">
            <div data-quick-create-focus={`drops.${stopNumber - 1}.place`}>
              <ReadOnly when={readOnlyLocation}>
                <group.AppField name="place">
                  {field => <field.CompanyLocationInput companyId={companyId} disabled={pending || companyId == null} label={shortcutHints ? <span className="flex items-center gap-2">
                            <span>Location *</span>
                            <Kbd>⇧L</Kbd>
                          </span> : 'Location'} nearbyCoordinates={nearbyCoordinates} onAddNewLocation={({
                  googlePlace,
                  query
                }) => createLocation({
                  googlePlace,
                  query
                })} onEditLocation={editLocation} onGoogleLocationSelect={googlePlace => createLocation({
                  googlePlace,
                  query: googlePlace.place_name
                })} onPastedLocationSelect={pastedLocation => createLocation({
                  pastedLocation,
                  query: pastedLocation.kind === 'google-location' ? pastedLocation.googlePlace.place_name : `${pastedLocation.coordinates.lat}, ${pastedLocation.coordinates.lng}`
                })} orgIdOverride={organisationId} restoreFocusOnSelect showAddress={false} triggerRef={locationTriggerRef} />}
                </group.AppField>
              </ReadOnly>
            </div>
            <div data-quick-create-focus={`drops.${stopNumber - 1}.reference_no`}>
              <group.Field name="reference_no">
                {field => <Field className="gap-1">
                    <FieldLabel htmlFor={`quick-create-stop-${stopId}-reference`}>
                      Reference (optional) {shortcutHints ? <Kbd>⇧N</Kbd> : null}
                    </FieldLabel>
                    <FieldContent>
                      <Input className="bg-background shadow-none" disabled={pending} id={`quick-create-stop-${stopId}-reference`} onBlur={field.handleBlur} onChange={event => field.handleChange(event.target.value || null)} value={field.state.value ?? ''} />
                      <FieldError errors={field.state.meta.errors} />
                    </FieldContent>
                  </Field>}
              </group.Field>
            </div>
            <fieldset className="grid min-w-0 gap-2 @min-[34rem]/quick-create:col-span-2 @min-[34rem]/quick-create:grid-cols-2 @min-[54rem]/quick-create:col-span-2">
              <legend className="sr-only">Stop {stopNumber} date and time</legend>
              <div data-quick-create-focus={`drops.${stopNumber - 1}.date`}>
                <group.AppField listeners={{
                onChange: ({
                  value
                }) => onDateChange?.(value)
              }} name="date">
                  {field => <field.DatePicker autoFocus calendarDisable={minDate ? {
                  before: minDate
                } : undefined} contentClassName="gap-1" disabled={pending} label={shortcutHints ? <span className="flex items-center gap-2">
                            <span>Date</span>
                            <Kbd>⇧D</Kbd>
                          </span> : 'Date'} required={dateRequired} triggerClassName="w-full justify-start" triggerRef={dateTriggerRef} />}
                </group.AppField>
              </div>
              <div data-quick-create-focus={`drops.${stopNumber - 1}.time`}>
                <group.Field name="time">
                  {field => <QuickCreateTimeControl disabled={pending} errors={field.state.meta.errors ?? []} onBlur={field.handleBlur} onChange={field.handleChange} onOpenChange={onOpenTimeMenuChange} onSelectTimeType={onSelectTimeType} open={timeMenuOpen} shortcutHints={shortcutHints} stopId={stopId} stopNumber={stopNumber} timeTypes={timeTypes} value={field.state.value} />}
                </group.Field>
              </div>
            </fieldset>
          </div>
        </fieldset>
      </section>;
  }
});